import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VEHICLE_TYPES } from '@/features/provisioning/vehicleFields';
import { VehicleTypePreview } from '@/features/provisioning/VehicleTypePreview';
import type { AdminVehicle } from './useAdminVehicles';
import { classifyEditVehicleError, useEditVehicle, type EditVehiclePatch } from './useEditVehicle';

/**
 * SLICE 56b — edit a vehicle's customer-facing IDENTITY (plate, name/label, body
 * type). Super-admin only, opened solely from the super-admin action in the
 * `Vehicles` detail dialog. The customer app is deliberately read-only for these
 * fields (absence over disabled), so this is the only door to them after
 * provisioning — Mahad needs MOG001 → M6D826 and its type → Bajaj (three_wheeler).
 *
 * Mirrors `EditAccountNameDialog` (slice 55): a modal over the list, the fields
 * PREFILLED with the stored values, and a submit disabled until SOMETHING actually
 * differs. It sends ONLY the changed fields (a partial patch), so an unchanged field
 * is never written and a stray key can never reach the `.strict()` route.
 *
 * 🔴 THE TYPE `<select>` REUSES `VEHICLE_TYPES` + `provision.type.*` — the one
 * provisioning list, never a second copy (`vehicleTypes.test.ts` guards it against
 * the DB enum). `three_wheeler` (Bajaj) is in that list and is therefore selectable.
 *
 * The client does a low floor only (plate non-empty, something changed); the SERVER
 * is the real validator (trims, bounds plate to 32 and label to 120, validates the
 * enum, 404s an unknown/malformed id), and the error copy comes from
 * `classifyEditVehicleError`.
 */

const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary';

export function EditVehicleDialog({
  vehicle,
  onClose,
}: {
  vehicle: AdminVehicle;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  // Baselines: the stored values, with the nullable ones normalised so the diff and
  // the controls have a concrete value. A null type shows as `other` (the
  // provisioning default), so leaving it alone never sets a type as a side effect.
  const plate0 = vehicle.plate;
  const label0 = vehicle.label ?? '';
  const model0 = vehicle.model ?? '';
  const type0 = vehicle.vehicleType ?? 'other';

  const [plate, setPlate] = useState(plate0);
  const [label, setLabel] = useState(label0);
  const [model, setModel] = useState(model0);
  const [vehicleType, setVehicleType] = useState(type0);
  // SLICE 83 — the device's reporting period, in seconds, prefilled from the admin
  // vehicle payload (`reportPeriodSec`, seam S3). It is only sent when the operator
  // actually edits it: that way a plate-only edit never rewrites the period as a side
  // effect. Blank = use the 300 s default (null on the wire).
  const reportPeriod0 = vehicle.reportPeriodSec != null ? String(vehicle.reportPeriodSec) : '';
  const [reportPeriod, setReportPeriod] = useState(reportPeriod0);
  const [reportPeriodTouched, setReportPeriodTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ plate: string } | null>(null);
  const edit = useEditVehicle();

  const plateT = plate.trim();
  const labelT = label.trim();
  const modelT = model.trim();
  const plateChanged = plateT !== plate0;
  const labelChanged = labelT !== label0;
  const modelChanged = modelT !== model0;
  const typeChanged = vehicleType !== type0;
  // Reporting period: blank clears to the default (null); otherwise a whole number in
  // 5–3600. The server is the real validator (its own 5..3600), but a low client
  // floor stops an obviously-bad value before a round trip.
  const reportPeriodTrim = reportPeriod.trim();
  const reportPeriodNum = reportPeriodTrim === '' ? null : Number(reportPeriodTrim);
  const reportPeriodValid =
    reportPeriodNum === null ||
    (Number.isInteger(reportPeriodNum) && reportPeriodNum >= 5 && reportPeriodNum <= 3600);
  // A real, DIFFERENT edit, and a plate is always required to be non-empty (the
  // server enforces min 1 too). An unchanged form is a no-op the operator did not
  // mean to submit.
  const ready =
    plateT.length > 0 &&
    reportPeriodValid &&
    (plateChanged || labelChanged || modelChanged || typeChanged || reportPeriodTouched);

  async function submit() {
    if (!ready) return;
    setError(null);
    // Send ONLY the changed fields (partial patch). `label`/`model` send their trimmed
    // value even when '' — that is the blank-it intent, which the server maps to null.
    const patch: EditVehiclePatch = {};
    if (plateChanged) patch.plate = plateT;
    if (labelChanged) patch.label = labelT;
    if (modelChanged) patch.model = modelT;
    if (typeChanged) patch.vehicleType = vehicleType;
    // Only when the operator touched it — blank sends null (use the default).
    if (reportPeriodTouched) patch.reportPeriodSec = reportPeriodNum;
    try {
      const result = await edit.mutateAsync({ vehicleId: vehicle.vehicleId, patch });
      setDone({ plate: result.plate });
    } catch (e) {
      setError(t(`editVehicle.error.${classifyEditVehicleError(e)}`));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('editVehicle.title', { plate: vehicle.plate })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-md sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('editVehicle.title', { plate: vehicle.plate })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('editVehicle.close')}
          </button>
        </div>

        {done ? (
          <div>
            <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12.5px]">
              {t('editVehicle.done', { plate: done.plate })}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white"
              >
                {t('editVehicle.doneButton')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12px] text-raad-muted">
              {t('editVehicle.intro')}
            </p>

            <label className="block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('editVehicle.plate')}
              </span>
              <input
                className={INPUT}
                value={plate}
                onChange={(e) => {
                  setPlate(e.target.value);
                  setError(null);
                }}
                autoFocus
                autoComplete="off"
              />
            </label>

            <label className="mt-3 block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('editVehicle.label')}
              </span>
              <input
                className={INPUT}
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                  setError(null);
                }}
                autoComplete="off"
              />
            </label>

            <label className="mt-3 block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('editVehicle.model')}
              </span>
              <input
                className={INPUT}
                value={model}
                onChange={(e) => {
                  setModel(e.target.value);
                  setError(null);
                }}
                autoComplete="off"
              />
            </label>

            <label className="mt-3 block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('editVehicle.vehicleType')}
              </span>
              {/* Slice 58: correcting the type shows the marker it will draw. */}
              <span className="flex items-center gap-3">
                <select
                  className={INPUT}
                  value={vehicleType}
                  onChange={(e) => {
                    setVehicleType(e.target.value);
                    setError(null);
                  }}
                >
                  {VEHICLE_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {t(`provision.type.${v}`)}
                    </option>
                  ))}
                </select>
                <VehicleTypePreview vehicleType={vehicleType} label={t(`provision.type.${vehicleType}`)} />
              </span>
            </label>

            <label className="mt-3 block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('editVehicle.reportPeriod')}
              </span>
              <input
                className={INPUT}
                type="number"
                min={5}
                max={3600}
                inputMode="numeric"
                value={reportPeriod}
                placeholder="300"
                onChange={(e) => {
                  setReportPeriod(e.target.value);
                  setReportPeriodTouched(true);
                  setError(null);
                }}
                autoComplete="off"
              />
              <span className="mt-1 block text-[11px] text-raad-muted">
                {t('editVehicle.reportPeriodHint')}
              </span>
              {!reportPeriodValid ? (
                <span role="alert" className="mt-1 block text-[11px] text-red-400">
                  {t('editVehicle.reportPeriodRange')}
                </span>
              ) : null}
            </label>

            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={edit.isPending}
                className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
              >
                {t('editVehicle.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={!ready || edit.isPending}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {edit.isPending ? t('editVehicle.saving') : t('editVehicle.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
